import React from "react";
import { Zap, TrendingUp, Search, BarChart3, Smartphone, Monitor, BookOpen } from "lucide-react";

/**
 * Rich visual stats for the "Tutti i dettagli tecnici rilevati" section:
 * 4 KPI cards, Lighthouse rings (mobile/desktop), domain metrics and keyword
 * distribution — same layout and copy as the v1 report (requested by Max).
 */

const fmt = (n) => (n == null ? "—" : String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, "."));
const eur = (n) => `€${fmt(n || 0)}`;

const ringColor = (s) => (s >= 90 ? "#15803d" : s >= 50 ? "#d97706" : "#dc2626");

function ScoreRing({ score, label }) {
  const s = Math.round(score || 0);
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className="v2t-ring" data-testid={`ring-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <svg width="68" height="68" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r={r} fill="none" stroke="#eef2f7" strokeWidth="6"/>
        <circle cx="34" cy="34" r={r} fill="none" stroke={ringColor(s)} strokeWidth="6"
                strokeLinecap="round" strokeDasharray={`${(c * s) / 100} ${c}`}
                transform="rotate(-90 34 34)"/>
        <text x="34" y="34" textAnchor="middle" dominantBaseline="central"
              fontSize="18" fontWeight="800" fill={ringColor(s)}>{s}</text>
      </svg>
      <div className="v2t-ring-lbl">{label}</div>
    </div>
  );
}

function Explainer({ title, children, testid }) {
  return (
    <div className="v2t-explainer" data-testid={testid}>
      <div className="v2t-explainer-h"><BookOpen size={15}/> {title}</div>
      <div className="v2t-explainer-b">{children}</div>
    </div>
  );
}

function KpiCard({ icon, label, value, suffix, sub, testid }) {
  return (
    <div className="v2t-kpi" data-testid={testid}>
      <div className="v2t-kpi-h"><span className="v2t-kpi-ico">{icon}</span> {label}</div>
      <div className="v2t-kpi-v">{value}{suffix && <span className="v2t-kpi-sfx">{suffix}</span>}</div>
      <div className="v2t-kpi-sub">{sub}</div>
    </div>
  );
}

export default function TechStatsBlocks({ m = {}, lhm = {}, lhd = {} }) {
  const kw = {
    top13: m.position_1to3 || 0,
    page1: (m.position_1to3 || 0) + (m.position_4to6 || 0) + (m.position_7to10 || 0),
    page2: Math.max(0, (m.page1 || 0) - ((m.position_1to3 || 0) + (m.position_4to6 || 0) + (m.position_7to10 || 0))),
    other: Math.max(0, (m.total_keywords || 0) - (m.page1 || 0)),
    total: m.total_keywords || 0,
  };

  return (
    <div className="v2t" data-testid="tech-stats-blocks">
      {/* ---- 4 KPI */}
      <div className="v2t-grid4">
        <KpiCard icon={<Zap size={15}/>} label="Autorevolezza" testid="v2t-kpi-authority"
                 value={Math.round(m.z_authority || 0)} suffix="/100" sub="indice del dominio"/>
        <KpiCard icon={<TrendingUp size={15}/>} label="Traffico" testid="v2t-kpi-traffic"
                 value={fmt(m.traffic_monthly ?? m.estimated_traffic)} sub="utenti/mese"/>
        <KpiCard icon={<Search size={15}/>} label="Parole Chiave" testid="v2t-kpi-keywords"
                 value={fmt(m.total_keywords)} sub="posizionate"/>
        <KpiCard icon={<BarChart3 size={15}/>} label="Valore Traffico" testid="v2t-kpi-value"
                 value={eur(m.traffic_value)} sub="equivalente Ads"/>
      </div>

      <Explainer title="Come leggere questi 4 numeri" testid="v2t-explainer-overview">
        <ul>
          <li><strong>Autorevolezza</strong>: indicatore <em>logaritmico</em> (scala compressa). Sotto 30 sei una "voce nuova" per Google; sopra 50 sei già un'autorità del settore.</li>
          <li><strong>Traffico</strong>: visite mensili stimate solo da ricerca organica (no social, no pubblicità).</li>
          <li><strong>Parole chiave</strong>: termini per cui appari nelle prime 100 posizioni Google. Più ne hai, più clienti potenziali ti trovano.</li>
          <li><strong>Valore Traffico</strong>: quanto pagheresti su Google Ads per ricevere lo stesso traffico. È il valore commerciale che il tuo sito sta producendo (o NON producendo).</li>
        </ul>
      </Explainer>

      {/* ---- Lighthouse rings */}
      <div className="v2t-grid2">
        <div className="v2t-card">
          <div className="v2t-card-h"><Smartphone size={15}/> Performance Mobile</div>
          <div className="v2t-ring-row">
            <ScoreRing score={lhm.performance} label="Performance"/>
            <ScoreRing score={lhm.accessibility} label="Accessibilità"/>
            <ScoreRing score={lhm.best_practices} label="Best Practices"/>
            <ScoreRing score={lhm.seo} label="SEO"/>
          </div>
        </div>
        <div className="v2t-card">
          <div className="v2t-card-h"><Monitor size={15}/> Performance Desktop</div>
          <div className="v2t-ring-row">
            <ScoreRing score={lhd.performance} label="Performance"/>
            <ScoreRing score={lhd.accessibility} label="Accessibilità"/>
            <ScoreRing score={lhd.best_practices} label="Best Practices"/>
            <ScoreRing score={lhd.seo} label="SEO"/>
          </div>
        </div>
      </div>

      <Explainer title="Come leggere gli anelli colorati" testid="v2t-explainer-rings">
        I 4 anelli sono il <strong>punteggio Google Lighthouse</strong> (lo stesso strumento che usi su
        PageSpeed Insights). Sono valori indipendenti uno dall'altro: un sito può essere veloce ma poco
        accessibile, o ottimo per la SEO ma lento.
        <ul>
          <li><strong>Performance</strong>: quanto è veloce il sito. ≥90 ottimo · 50-89 da migliorare · &lt;50 critico.</li>
          <li><strong>Accessibilità</strong>: quanto è usabile da persone con disabilità (screen reader, contrasti, navigazione tastiera).</li>
          <li><strong>Best Practices</strong>: uso di HTTPS, librerie aggiornate, sicurezza, console errors.</li>
          <li><strong>SEO</strong>: title, meta-description, struttura headings, mobile-friendly.</li>
        </ul>
        <em>Il PageSpeed Mobile è quello che conta di più: Google indicizza prima la versione mobile e
        oltre il 60% delle ricerche avviene da smartphone.</em>
      </Explainer>

      {/* ---- Domain metrics */}
      <div className="v2t-card">
        <div className="v2t-card-h"><TrendingUp size={15}/> Metriche di Dominio</div>
        <div className="v2t-grid4 v2t-mini-row">
          <div className="v2t-mini v2t-mini-orange" data-testid="v2t-mini-authority">
            <div className="v2t-mini-n">{Math.round(m.z_authority || 0)}</div>
            <div className="v2t-mini-l">Autorevolezza</div>
          </div>
          <div className="v2t-mini v2t-mini-emerald" data-testid="v2t-mini-trust">
            <div className="v2t-mini-n">{Math.round(m.z_trust || 0)}</div>
            <div className="v2t-mini-l">Fiducia</div>
          </div>
          <div className="v2t-mini v2t-mini-blue" data-testid="v2t-mini-stability">
            <div className="v2t-mini-n">{Math.round(m.z_stability || 0)}</div>
            <div className="v2t-mini-l">Stabilità</div>
          </div>
          <div className="v2t-mini v2t-mini-violet" data-testid="v2t-mini-opportunity">
            <div className="v2t-mini-n">{Math.round(m.z_opportunity || 0)}</div>
            <div className="v2t-mini-l">Opportunità</div>
          </div>
        </div>
        <Explainer title="Le 4 dimensioni del dominio" testid="v2t-explainer-domain">
          <ul>
            <li><strong>Autorevolezza</strong>: peso del dominio agli occhi di Google (link, anzianità, coerenza tematica).</li>
            <li><strong>Fiducia</strong>: assenza di tecniche penalizzabili (spam, link tossici, manipolazioni).</li>
            <li><strong>Stabilità</strong>: costanza del traffico nel tempo. Punteggio basso = ranking ballerino.</li>
            <li><strong>Opportunità</strong>: keyword "vicine alla top 10" che con poco lavoro possono entrare in prima pagina.</li>
          </ul>
        </Explainer>
      </div>

      {/* ---- Keyword distribution */}
      <div className="v2t-card">
        <div className="v2t-card-h"><Search size={15}/> Distribuzione Keyword</div>
        <div className="v2t-grid5">
          <div className="v2t-dist v2t-d-emerald" data-testid="v2t-dist-top13">
            <div className="v2t-dist-v">{fmt(kw.top13)}</div><div className="v2t-dist-l">Top 1-3</div>
          </div>
          <div className="v2t-dist v2t-d-teal" data-testid="v2t-dist-page1">
            <div className="v2t-dist-v">{fmt(kw.page1)}</div><div className="v2t-dist-l">1° Pag.</div>
          </div>
          <div className="v2t-dist v2t-d-amber" data-testid="v2t-dist-page2">
            <div className="v2t-dist-v">{fmt(kw.page2)}</div><div className="v2t-dist-l">2° Pag.</div>
          </div>
          <div className="v2t-dist v2t-d-orange" data-testid="v2t-dist-other">
            <div className="v2t-dist-v">{fmt(kw.other)}</div><div className="v2t-dist-l">Altre</div>
          </div>
          <div className="v2t-dist v2t-d-slate" data-testid="v2t-dist-total">
            <div className="v2t-dist-v">{fmt(kw.total)}</div><div className="v2t-dist-l">Totale</div>
          </div>
        </div>
        <Explainer title="Dove ti trovano i clienti" testid="v2t-explainer-kwdist">
          Ogni numero rappresenta quante parole chiave ti posizionano in quella zona Google:
          <ul>
            <li><strong>Top 1-3</strong>: prime 3 posizioni → il 60-70% dei click va qui. Sono le keyword che producono traffico VERO.</li>
            <li><strong>1° Pagina (4-10)</strong>: visibili ma sotto la piega. Click sporadici.</li>
            <li><strong>2° Pagina (11-20)</strong>: praticamente invisibili. &lt;5% degli utenti scrolla fin qui.</li>
            <li><strong>Altre (21-100)</strong>: irrilevanti per il traffico, ma sono il "serbatoio" su cui lavorare per farle salire.</li>
          </ul>
          <em>L'obiettivo della SEO è spostare keyword dalla colonna "Altre" verso "Top 1-3": ogni salto
          fa crescere il traffico in modo non lineare.</em>
        </Explainer>
      </div>

      <style>{`
        .v2t { font-family: 'Montserrat', sans-serif; }
        .v2t * { box-sizing: border-box; }
        .v2t-grid4 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
        .v2t-grid5 { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
        .v2t-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-top: 22px; }
        .v2t-kpi {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 18px 20px;
          box-shadow: 0 1px 3px rgba(15,23,42,.04);
        }
        .v2t-kpi-h {
          display: flex; align-items: center; gap: 9px; font-size: 13.5px; font-weight: 700;
          color: #334155;
        }
        .v2t-kpi-ico {
          display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px;
          border-radius: 8px; background: #fdf6f0; color: #c95a1a;
        }
        .v2t-kpi-v { font-size: 30px; font-weight: 800; color: #0f172a; margin-top: 12px; line-height: 1; }
        .v2t-kpi-sfx { font-size: 14px; color: #64748b; font-weight: 700; }
        .v2t-kpi-sub { font-size: 12.5px; color: #64748b; margin-top: 6px; }
        .v2t-explainer {
          background: #f0f7ff; border: 1px solid #cfe2fb; border-left: 4px solid #2563eb;
          border-radius: 12px; padding: 16px 20px; margin: 18px 0 0;
        }
        .v2t-explainer-h {
          display: flex; align-items: center; gap: 8px; color: #1d4ed8; font-weight: 800;
          font-size: 11.5px; letter-spacing: .1em; text-transform: uppercase;
        }
        .v2t-explainer-b { font-size: 14px; line-height: 1.6; color: #0f172a; margin-top: 10px; }
        .v2t-explainer-b ul { margin: 6px 0 0; padding-left: 22px; list-style: disc outside; }
        .v2t-explainer-b li { margin: 5px 0; display: list-item; }
        .v2t-explainer-b li::marker { color: #2563eb; }
        .v2t-explainer-b strong { color: #1d4ed8; }
        .v2t-explainer-b > em { display: block; margin-top: 8px; color: #334155; }
        .v2t-card {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 20px 22px;
          margin-top: 22px;
        }
        .v2t-card-h {
          display: flex; align-items: center; gap: 9px; font-size: 15px; font-weight: 800;
          margin-bottom: 18px; color: #0f172a;
        }
        .v2t-ring-row { display: flex; justify-content: space-around; gap: 10px; flex-wrap: wrap; }
        .v2t-ring { text-align: center; }
        .v2t-ring-lbl { font-size: 11.5px; color: #64748b; margin-top: 6px; font-weight: 600; }
        .v2t-mini-row { margin-bottom: 4px; }
        .v2t-mini { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 12px; padding: 14px; text-align: center; }
        .v2t-mini-n { font-size: 26px; font-weight: 800; line-height: 1; }
        .v2t-mini-l { font-size: 12px; color: #64748b; margin-top: 6px; font-weight: 600; }
        .v2t-mini-orange .v2t-mini-n { color: #c2410c; }
        .v2t-mini-emerald .v2t-mini-n { color: #15803d; }
        .v2t-mini-blue .v2t-mini-n { color: #1d4ed8; }
        .v2t-mini-violet .v2t-mini-n { color: #7c3aed; }
        .v2t-dist { border-radius: 12px; padding: 14px 10px; text-align: center; border: 1px solid; }
        .v2t-dist-v { font-size: 24px; font-weight: 800; line-height: 1; }
        .v2t-dist-l { font-size: 11.5px; margin-top: 6px; font-weight: 700; color: #475569; }
        .v2t-d-emerald { background: #f0fdf4; border-color: #bbf7d0; }
        .v2t-d-emerald .v2t-dist-v { color: #15803d; }
        .v2t-d-teal { background: #effcf8; border-color: #b6ece0; }
        .v2t-d-teal .v2t-dist-v { color: #0f766e; }
        .v2t-d-amber { background: #fffbeb; border-color: #fde68a; }
        .v2t-d-amber .v2t-dist-v { color: #b45309; }
        .v2t-d-orange { background: #fff7ed; border-color: #fed7aa; }
        .v2t-d-orange .v2t-dist-v { color: #c2410c; }
        .v2t-d-slate { background: #f8fafc; border-color: #e2e8f0; }
        .v2t-d-slate .v2t-dist-v { color: #334155; }
        @media (max-width: 860px) {
          .v2t-grid4 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
          .v2t-grid2 { grid-template-columns: minmax(0, 1fr); }
          .v2t-grid5 { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
          .v2t-kpi { padding: 14px; }
          .v2t-kpi-v { font-size: 24px; margin-top: 8px; }
          .v2t-card { padding: 16px 14px; }
          .v2t-explainer { padding: 14px 16px; }
          .v2t-explainer-b { font-size: 13.5px; }
          .v2t-ring-row { gap: 4px; }
        }
      `}</style>
    </div>
  );
}
